import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { InterruptHostRef, PostLoginInterrupt } from './post-login-interrupt.model';
import { InterruptQueue } from './interrupt-queue';
import { InterruptCollector } from './interrupt-collector';
import { createSettledStore } from './interrupt-settled-store';
import { InterruptFrameComponent } from '../../../shared/components/interrupt-frame/interrupt-frame.component';

/**
 * Dismissal reason the queue uses on ITS OWN modal when it needs to re-present the same
 * items in a different shape (one card became two, so the frame takes over). It must
 * never be mistaken for the investor putting something off, or `onDefer` would fire and
 * the post-login navigation would run early.
 */
export const REPRESENTING = 'INC-327:representing';

/**
 * INC-329 — how long a collection round waits for a slow source before presenting whatever
 * has arrived. Short enough that a flagged investor is not left staring at nothing, long
 * enough to cover an ordinary round-trip for the pending-vote call. The timeout is a safety
 * net, not the normal path: both sources report and close the round themselves.
 *
 * KNOWN RESIDUAL, accepted deliberately (INC-329 review). This BOUNDS the open-then-swap
 * window; it does not eliminate it. A source answering AFTER the timeout has released still
 * enqueues, and the queue re-presents — so a bare card can still be restructured into the
 * frame at, say, 3s. Suppressing that was considered and rejected: it would mean silently
 * DROPPING a late interrupt until the next login, and it would regress INC-328's
 * "a second interrupt arriving late joins the frame" behaviour. Showing a late item beats
 * losing it, and in practice both sources report well inside the window.
 */
export const COLLECTION_TIMEOUT_MS = 2500;

/**
 * INC-327 — one priority-ordered queue behind every post-login popup.
 *
 * Three call sites register here instead of calling `NgbModal.open()` themselves:
 * ordinary login, the pending-vote check in `AppComponent`, and login-on-behalf.
 *
 * The queue owns the LIFETIME of what it opens. That is the fix for the blanket
 * `dismissAll()` on `NavigationEnd`, which did not merely wipe interrupts on later
 * navigation — it killed the popup on first paint, because the initial `NavigationEnd`
 * lands after the popup has already opened. `AppComponent` now calls
 * {@link dismissOtherModals} instead, which dismisses every OTHER open modal exactly as
 * before and leaves this one alone. No reviving, no reopening.
 *
 * This service is deliberately invisible on its own: with one interrupt queued — the
 * only state production can currently produce — it opens that body as its own modal with
 * that call site's original options, so the investor sees precisely what shipped before.
 * INC-328 adds the frame on top, for two or more.
 *
 * Ordering and settle-once live in {@link InterruptQueue}; this class is the Angular
 * adapter that turns queue state into an open modal.
 */
@Injectable({ providedIn: 'root' })
export class PostLoginInterruptService {
  private readonly queue = new InterruptQueue(createSettledStore());

  /** INC-329 — holds presentation while the round's sources are still reporting. */
  private readonly collector = new InterruptCollector();

  private collectionTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Every modal ng-bootstrap currently has open, ours included.
   *
   * `activeInstances` emits the stack's own array and mutates it in place, so anything
   * that iterates has to copy first.
   */
  private openModals: NgbModalRef[] = [];

  /** The modal this queue opened, if any. Never dismissed by {@link dismissOtherModals}. */
  private ownModal: NgbModalRef | null = null;

  /** What `ownModal` is currently showing, so an unchanged queue doesn't reopen it. */
  private presentedSignature: string | null = null;

  private presentScheduled = false;

  constructor(private readonly modalService: NgbModal) {
    this.modalService.activeInstances.subscribe((refs: NgbModalRef[]) => {
      this.openModals = refs ?? [];
    });
  }

  /**
   * Register an interrupt. Safe to call from anywhere, at any time — the queue decides
   * what is on screen and re-presents itself if the set changes.
   */
  enqueue(interrupt: PostLoginInterrupt): void {
    if (this.queue.enqueue(interrupt)) {
      this.schedulePresent();
    }
  }

  /**
   * INC-329 — open a collection round before asking the sources.
   *
   * Every interrupt is now raised after the investor has landed, so both sources are asked
   * at once and answer at their own speed. Without this, the first answer would open as a
   * bare card and be swapped for the frame when the second landed — the visible
   * "open then swap" this story exists to remove. Nothing presents until every source has
   * called {@link sourceReported}, or `timeoutMs` elapses.
   *
   * `timeoutMs` is the guarantee that a slow or failed source cannot leave a flagged
   * investor with nothing on screen.
   */
  beginCollection(sources: string[], timeoutMs: number = COLLECTION_TIMEOUT_MS): void {
    this.clearCollectionTimer();
    this.collector.begin(sources);
    if (!this.collector.isCollecting) {
      return;
    }
    this.collectionTimer = setTimeout(() => this.releaseCollection(), timeoutMs);
  }

  /**
   * A source has an answer — whether or not it queued anything. A source with nothing to
   * add MUST still report, or the round would only ever end on its timeout.
   */
  sourceReported(source: string): void {
    if (this.collector.report(source)) {
      this.clearCollectionTimer();
      this.schedulePresent();
    }
  }

  /**
   * Start a fresh session: drop the queue AND everything settled, including the persisted
   * dismissals. Called on every successful login, which is what makes "the item returns on
   * the next login" true even when the investor logs out and back in within one tab —
   * session storage on its own would wrongly keep suppressing it (INC-329).
   */
  startSession(): void {
    this.reset();
  }

  /** The queue in the order the investor will meet it. */
  get pending(): PostLoginInterrupt[] {
    return this.queue.pending;
  }

  /**
   * Dismiss every modal EXCEPT this queue's — the replacement for the blanket
   * `modalService.dismissAll()` on `NavigationEnd`.
   *
   * Behaviour for every other modal in the app is unchanged: each open ref is dismissed
   * with the same (absent) reason `dismissAll()` used.
   */
  dismissOtherModals(reason?: unknown): void {
    const own = this.ownModal;
    for (const ref of [...this.openModals]) {
      if (ref !== own) {
        ref.dismiss(reason);
      }
    }
  }

  /**
   * Hand a hosted body its outcome channel (INC-328). On the single-interrupt path no
   * host is passed and the body keeps talking to its own `NgbActiveModal`, exactly as it
   * does today.
   */
  hostRefFor(interrupt: PostLoginInterrupt): InterruptHostRef {
    return {
      resolve: () => this.settle(interrupt.key, 'resolve'),
      defer: () => this.settle(interrupt.key, 'defer'),
    };
  }

  /**
   * Drop the queue without running any callback — for a session that ended under it.
   *
   * Abandons the collection round too. `InterruptCollector.release()` documents itself as
   * being for exactly this ("the timeout, or a session ending underneath us") and this was
   * the one caller that did not honour it: `reset()` cleared the queue and left
   * `isCollecting` true with the round's timer still armed, so a timeout belonging to the
   * previous session could still fire, and `schedulePresent()` — which returns early while
   * a round is open — was held on a round that had no sources left to report.
   *
   * `startSession()` already did the full teardown by hand. Having it delegate here is what
   * stops the two drifting apart again.
   */
  reset(): void {
    this.clearCollectionTimer();
    this.collector.release();
    this.queue.clear();
    this.closeOwnModal();
  }

  /**
   * The round's safety net fired, or it is being abandoned. Presents once — and only if a
   * round really was open, so a timeout that lands just after the last source reported
   * cannot present a second time.
   */
  private releaseCollection(): void {
    this.clearCollectionTimer();
    if (this.collector.release()) {
      this.schedulePresent();
    }
  }

  private clearCollectionTimer(): void {
    if (this.collectionTimer !== null) {
      clearTimeout(this.collectionTimer);
      this.collectionTimer = null;
    }
  }

  private settle(key: string, outcome: 'resolve' | 'defer'): void {
    if (this.queue.settle(key, outcome)) {
      this.schedulePresent();
    }
  }

  private deferAll(): void {
    this.queue.deferAll();
    this.schedulePresent();
  }

  /**
   * Batch presentation to the end of the current microtask, so two interrupts that
   * arrive together open one thing rather than one and then the other.
   */
  private schedulePresent(): void {
    // INC-329: held while a round is open. The release path calls this again, so nothing
    // is lost — it is only deferred until the queue is known to be complete.
    if (this.collector.isCollecting) {
      return;
    }
    if (this.presentScheduled) {
      return;
    }
    this.presentScheduled = true;
    Promise.resolve().then(() => {
      this.presentScheduled = false;
      this.present();
    });
  }

  private present(): void {
    const ordered = this.queue.pending;

    if (!ordered.length) {
      this.closeOwnModal();
      return;
    }

    const signature = this.queue.signature;
    if (this.ownModal && this.presentedSignature === signature) {
      return;
    }

    this.closeOwnModal();
    this.presentedSignature = signature;
    // One interrupt renders as its own modal, exactly as it did before this queue
    // existed — which is what makes "no cover and no frame chrome at all" on the
    // single-item path structural rather than a branch inside the frame (INC-328).
    this.ownModal = ordered.length > 1 ? this.openFrame(ordered) : this.openSingle(ordered[0]);
  }

  /**
   * INC-328 — two or more interrupts share one frame: a cover listing everything
   * waiting, then a paged deck. The bodies are reused unforked and handed a host ref, so
   * their buttons resolve one item instead of tearing the frame down.
   *
   * Closing the frame (×, Esc, backdrop) with items left on it defers each of them, so
   * every call site's `onDefer` still runs exactly once — including login's, which is
   * what completes the post-login navigation.
   */
  private openFrame(ordered: PostLoginInterrupt[]): NgbModalRef {
    // A queued MANDATORY gate must be exactly as undismissable inside the frame as it is on
    // the single-interrupt path, where that item's own modalOptions already set these two —
    // otherwise Esc/backdrop-click on the frame silently defers it the moment a second,
    // dismissible interrupt happens to be queued alongside it.
    // InterruptFrameComponent.close() pairs with this by refusing the × for the same reason.
    // Keyed on `mandatory`, NOT `kind === 'gate'` — the pre-existing profile-refresh nudge is
    // also `kind: 'gate'` (it just outranks dismissible items) but explicitly supports
    // "Remind me later" via Esc/backdrop on its own modal; gating this on `kind` alone would
    // silently take that away the moment it shares a frame with anything else.
    const hasMandatoryGate = ordered.some((interrupt) => interrupt.mandatory);
    const modalRef = this.modalService.open(InterruptFrameComponent, {
      centered: true,
      windowClass: 'interrupt-frame-modal',
      ariaLabelledBy: 'interrupt-frame-title',
      ...(hasMandatoryGate ? { backdrop: 'static' as const, keyboard: false } : {}),
    });
    modalRef.componentInstance.items = ordered;
    modalRef.componentInstance.hostRefFor = (interrupt: PostLoginInterrupt) =>
      this.hostRefFor(interrupt);

    modalRef.result.then(
      () => this.afterOwnModalClosed(modalRef, () => this.deferAll()),
      (reason) => {
        if (reason === REPRESENTING) {
          return;
        }
        this.afterOwnModalClosed(modalRef, () => this.deferAll());
      },
    );

    return modalRef;
  }

  /**
   * The single-interrupt path — the body IS the modal, opened with that call site's own
   * options and its own `NgbActiveModal`. Nothing about this render differs from before
   * the queue existed.
   */
  private openSingle(interrupt: PostLoginInterrupt): NgbModalRef {
    const modalRef = this.modalService.open(
      interrupt.component,
      interrupt.modalOptions ?? { centered: true },
    );
    Object.assign(modalRef.componentInstance, interrupt.inputs ?? {});

    modalRef.result.then(
      () => this.afterOwnModalClosed(modalRef, () => this.settle(interrupt.key, 'resolve')),
      (reason) => {
        if (reason === REPRESENTING) {
          return;
        }
        this.afterOwnModalClosed(modalRef, () => this.settle(interrupt.key, 'defer'));
      },
    );

    return modalRef;
  }

  private afterOwnModalClosed(closed: NgbModalRef, then: () => void): void {
    if (this.ownModal === closed) {
      this.ownModal = null;
      this.presentedSignature = null;
    }
    then();
  }

  /** Close our own modal without any outcome — we are about to reopen in another shape. */
  private closeOwnModal(): void {
    const ref = this.ownModal;
    this.ownModal = null;
    this.presentedSignature = null;
    ref?.dismiss(REPRESENTING);
  }
}
